import { Avatar, Box, Typography } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import { AvatarGroup } from "@material-ui/lab";
import { D } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import { joinEnglishPick } from "../../../util";
import CartProduct from "../CartProduct";
import NoProducts from "./NoProducts";

const GreyText = styled("span")(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.grey[500],
}));

const StyledGroup = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  "&.p-1": {
    padding: theme.spacing(1),
  },
  "&.p-2": {
    padding: theme.spacing(2),
  },
  "&.p-3": {
    padding: theme.spacing(3),
  },
}));

export default function CartGrouping({
  shipmentGroups = [],
  cartProducts = [],
  canEdit = false,
  Container = "div",
  groupPadding = 3,
}) {
  const products = React.useMemo(() => {
    const allProducts = shipmentGroups.flatMap((group) =>
      group.products.map(({ cartProductId }) => {
        const cartProduct = cartProducts.find(
          (product) => product.cartProductId === cartProductId,
        );

        const fulfillers = [...(cartProduct.fulfillers || [])].filter(
          (fulfiller) => !fulfiller.no_fulfillment,
        );

        const key = fulfillers
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(D.get("influencer_id"))
          .join("-");

        return {
          cartProduct,
          key,
          title: joinEnglishPick(D.get("name"), fulfillers, { max: 3 }),
          avatars: (
            <AvatarGroup key={key} max={3}>
              {fulfillers.map((fulfiller) => (
                <Avatar
                  key={`${key}-${fulfiller.influencer_id}`}
                  src={fulfiller.avatar_url}
                  alt={fulfiller.name}
                />
              ))}
            </AvatarGroup>
          ),
        };
      }),
    );

    const byKey = allProducts.reduce((result, item) => {
      const existing = result[item.key];

      if (!existing) {
        result[item.key] = {
          avatars: item.avatars,
          key: item.key,
          products: [item.cartProduct],
          title: item.title,
        };
      } else {
        existing.products.push(item.cartProduct);
      }

      return result;
    }, {});

    return Object.values(byKey);
  }, [cartProducts, shipmentGroups]);

  if (!shipmentGroups.length) {
    return (
      <Container>
        <NoProducts groupPadding={groupPadding} />
      </Container>
    );
  }

  return (
    <Container>
      {products.length === 1 &&
        products.map((group, i) => (
          <StyledGroup className={`p-${groupPadding}`} key={i}>
            {group.products.map((cartProduct) => {
              if (!cartProduct) return null;
              return (
                <CartProduct
                  key={cartProduct.cartProductId}
                  cartProduct={cartProduct}
                  canEdit={canEdit}
                />
              );
            })}
          </StyledGroup>
        ))}

      {products.length > 1 &&
        products.map((group, i) => (
          <StyledGroup className={`p-${groupPadding}`} key={i}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box display="flex" alignItems="center" gridGap="16px">
                {group.avatars}
                <Typography component="h3" variant="subtitle1" gutterBottom>
                  {group.title}
                </Typography>
              </Box>

              <GreyText color="textSecondary" component="span">
                ({group.products.length}&nbsp;item
                {group.products.length > 1 ? "s" : ""})
              </GreyText>
            </Box>

            {group.products.map((cartProduct) => {
              if (!cartProduct) return null;
              return (
                <CartProduct
                  key={cartProduct.cartProductId}
                  cartProduct={cartProduct}
                  canEdit={canEdit}
                />
              );
            })}
          </StyledGroup>
        ))}
    </Container>
  );
}

CartGrouping.propTypes = {
  shipmentGroups: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          cartProductId: PropTypes.number.isRequired,
        }),
      ).isRequired,
      subtotal: PropTypes.number.isRequired,
      productBump: PropTypes.number,
      serviceFee: PropTypes.number,
      shippingCost: PropTypes.number,
      shippingRateAdjustment: PropTypes.number,
      tax: PropTypes.number,
      total: PropTypes.number.isRequired,
      itemized: PropTypes.object,
    }),
  ).isRequired,
  cartProducts: PropTypes.arrayOf(
    PropTypes.shape({
      cartProductId: PropTypes.number.isRequired,
    }),
  ).isRequired,
  canEdit: PropTypes.bool,
  Container: PropTypes.elementType,
  groupPadding: PropTypes.number,
};
